import type { LearnerProfileData } from '~/gql/users/learner-profile'
import { useTranslation } from 'react-i18next'
import { StatTile } from '~/components/StatTile'
import { useBranding } from '~/helpers/branding/branding-context'
import { DATE_WITH_YEAR, formatSessionDate, formatTimeOfDay } from '~/helpers/dates/session-dates'
import { formatSeconds } from '~/helpers/learner/duration'
import ProfileSection from './ProfileSection'

type ProfileIndicatorsProps = {
  profile: Pick<
    LearnerProfileData,
    'trainingSeconds' | 'loginCount' | 'lastLoginAt' | 'averageDaysBetweenLogins' | 'completedModules' | 'totalModules'
  >
}

const NONE = '—'
const SHORT_DATE: Intl.DateTimeFormatOptions = { ...DATE_WITH_YEAR, month: 'short' }

/** Les indicateurs de la fiche apprenant (#242) : temps, connexions, fréquence, progression. */
function ProfileIndicators({ profile }: ProfileIndicatorsProps) {
  const { t, i18n } = useTranslation()
  const { timezone } = useBranding()
  const { completedModules: done, totalModules: total } = profile

  return (
    <ProfileSection title={t('profile.indicators.title')} anchor="profile.indicators">
      <div className="grid grid-cols-[repeat(auto-fit,minmax(14rem,1fr))] gap-4">
        <StatTile
          label={t('profile.indicators.trainingTime')}
          value={formatSeconds(profile.trainingSeconds, t) ?? NONE}
          hint={t('profile.indicators.trainingTimeHint')}
        />
        <StatTile
          label={t('profile.indicators.logins')}
          value={profile.loginCount}
          hint={t('profile.indicators.loginsHint')}
        />
        {/* La date en chiffre, l'heure en précision : la tuile ne déborde pas sur trois lignes. */}
        <StatTile
          label={t('profile.indicators.lastLogin')}
          value={profile.lastLoginAt ? formatSessionDate(profile.lastLoginAt, i18n.language, timezone, SHORT_DATE) : NONE}
          hint={profile.lastLoginAt
            ? t('profile.indicators.lastLoginTime', { time: formatTimeOfDay(profile.lastLoginAt, i18n.language, timezone) })
            : t('profile.indicators.noLogin')}
        />
        <StatTile
          label={t('profile.indicators.frequency')}
          value={profile.averageDaysBetweenLogins != null
            ? t('profile.indicators.frequencyValue', { days: new Intl.NumberFormat(i18n.language).format(profile.averageDaysBetweenLogins) })
            : NONE}
          hint={profile.averageDaysBetweenLogins != null
            ? t('profile.indicators.frequencyHint')
            : t('profile.indicators.frequencyNone')}
        />
        <StatTile
          label={t('profile.indicators.progress')}
          value={total > 0 ? `${Math.round((done / total) * 100)} %` : NONE}
          hint={t('profile.indicators.progressHint', { done, total })}
        />
      </div>
    </ProfileSection>
  )
}

export default ProfileIndicators
